import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  InputNumber,
  Popover,
  Row,
  Spin,
  Statistic,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import ProgressGraph from "./ProgressGraph";
import moment from "moment";
import { getTargetString } from "../../helpers/targetHelpers";
import { getProgressColor } from "../../utils/progressColors";
import UpdateTargetApi from "../../api/target-progress/UpdateTargetApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import TargetProgressHistoryApi from "../../api/target-progress/TargetProgressHistoryApi";
import ProgressHistoryTable from "../tables/ProgressHistoryTable";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { permissionAbility } from "../../helpers/permissionHelpers";
import UpdateTargetProgressModal from "../modal/UpdateTargetProgressModal";

function PlanOverview(props) {
  const [submit, setSubmit] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [targetProgress, setTargetProgress] = useState(0);
  const [update, setUpdate] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const [targetAchived, setTargetAchieved] = useState(0);
  const [performance, setPerformance] = useState(0);
  let curLocation = useLocation();

  useEffect(() => {
    setTargetProgress(props.data?.targetAchieved || 0);
    setPerformance(props.data?.performance || 0);
    checkCanUpdate();
  }, [curLocation, props.data, props.data?.canUpdate, props.data?.cascadeFromSplitPlans, positionState?.id]);

  useEffect(() => {
    if (targetAchived) {
      setPerformance(((targetAchived / props.data?.target) * 100).toFixed(2));
    }
  }, [targetAchived, props.data?.target]);

  function checkCanUpdate() {
    // Use the backend's canUpdate property which already considers cascadeFromSplitPlans and cascadeFromTasks
    const backendCanUpdate = props.data?.canUpdate !== false; // Handle undefined as true
    const isAssignedUser = positionState?.id === props.data?.assignedTo?.positionId;
    const hasCascadeFromSplitPlans = props.data?.cascadeFromSplitPlans === true;
    const hasCascadeFromTasks = props.data?.cascadeFromTasks === true;
    
    // Disable if cascadeFromSplitPlans or cascadeFromTasks is true
    if (isAssignedUser && backendCanUpdate && !hasCascadeFromSplitPlans && !hasCascadeFromTasks) {
      setCanUpdate(true);
    } else {
      setCanUpdate(false);
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    
    // Calculate actual target value for percentage-based plans
    let actualTarget = values.target;
    
    // Check if this is a percentage-based plan
    const isPercentageUpdate = Boolean(
      props.data?.isPercentageUpdate === true || 
      props.data?.isPercentageUpdate === 1 || 
      props.data?.isPercentageUpdate === "1" ||
      props.data?.isPercentageUpdate === "true"
    );
    
    if (isPercentageUpdate) {
      // Convert percentage to actual amount
      // User enters 30%, we calculate 30% of the plan target
      actualTarget = (values.target / 100) * props.data?.target;
      console.log('Percentage calculation:', {
        userEnteredPercentage: values.target,
        planTarget: props.data?.target,
        calculatedActualTarget: actualTarget
      });
    }
    
    const apiResponse = await UpdateTargetApi(
      props.data?.planId,
      actualTarget, // Send calculated actual target, not percentage
      positionState?.id
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      if (apiResponse.targetProgress?.approvalStatus === "Approved") {
        openNotificationSuccess("bottomRight", `Target updated`);
        setTargetProgress(actualTarget); // Update with actual target
        setTargetAchieved(actualTarget); // Update with actual target
        props.updateTarget(actualTarget); // Update with actual target
      } else {
        openNotificationSuccess(
          "bottomRight",
          `Target updated but approval is pending`
        );
      }
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't update target. Try again later."
      );
    }
    setSubmit(false);
    setUpdate(false);
  }

  async function getProgressHistory() {
    setHistoryLoading(true);
    const apiResponse = await TargetProgressHistoryApi(props.data?.planId);
    if (apiResponse.message === "success") {
      setHistory(apiResponse);
      setHistoryLoading(false);
    }
  }

  function expandHistory() {
    setShowHistory(!showHistory);
    if (!history) {
      getProgressHistory();
    }
  }

  function renderAttributes() {
    return props.data?.planAttributeValue
      ?.sort((a, b) => a.name.localeCompare(b.name))
      .map((attribute) => {
        let displayValue = "-";
        
        if (attribute.valueType === "text") {
          displayValue = attribute.text || "-";
        } else if (attribute.valueType === "number") {
          displayValue = attribute.number || "-";
        } else if (attribute.valueType === "date") {
          displayValue = attribute.date 
            ? moment(attribute.date).format("DD.MM.YYYY") 
            : "-";
        } else if (attribute.valueType === "double") {
          displayValue = attribute.double !== null && attribute.double !== undefined
            ? attribute.double.toString()
            : "-";
        } else if (attribute.valueType === "dropdown") {
          // Use the resolved display value from backend
          displayValue = attribute.displayValue || attribute.value || "-";
        } else if (attribute.valueType === "boolean") {
          displayValue = attribute.value === true || attribute.value === "true" ? "Yes" : "No";
        }
        
        return (
          <Descriptions.Item
            key={attribute.id}
            label={attribute.name}
            className="w-1/2"
          >
            {displayValue}
          </Descriptions.Item>
        );
      });
  }

  return (
    <div className="pb-8">
      <div className="mb-4">
        <div className="text-lg mb-1">{props.data?.title}</div>{" "}
        <div className="flex items-center mb-1">
          Unit:&nbsp;
          <div
            className="w-2 h-2 mr-1 rounded-full"
            style={{ backgroundColor: props.data?.unit?.orgLevelColor }}
          />
          <div>{props.data?.unit?.name}</div>
          <Popover
            placement="top"
            content={
              <div className="text-xs">
                {props.data?.unit?.name}
                {props.data?.unit?.headUnit &&
                  " < " + props.data?.unit?.headUnit?.name}
                {props.data?.unit?.headUnit?.headUnit &&
                  " < " + props.data?.unit?.headUnit?.headUnit?.name}
              </div>
            }
            trigger="hover"
          >
            <div className="text-gray-500 text-xs ml-1">
              <InfoCircleOutlined />
            </div>
          </Popover>
        </div>
        <div className="flex items-center mb-1">
          User:&nbsp;
          {props.data?.assignedTo?.imageUrl ? (
            <Avatar
              className="cursor-pointer"
              src={props.data?.assignedTo?.imageUrl}
            />
          ) : (
            <Avatar>
              {props.data?.assignedTo?.firstName ? (
                props.data?.assignedTo?.firstName?.charAt(0).toUpperCase()
              ) : (
                <UserOutlined />
              )}
            </Avatar>
          )}
          <div className="ml-2">
            <div>
              {props.data?.assignedTo?.firstName
                ? props.data?.assignedTo?.firstName +
                  " " +
                  props.data?.assignedTo?.lastName
                : "-"}
            </div>
            <div className="text-xs -mt-1">
              {props.data?.assignedTo?.designationAbb}
            </div>
          </div>
        </div>
        {props.data?.planGroup && (
          <div className="flex items-center mb-1 gap-1">
            Plan group: <Tag>{props.data?.planGroup}</Tag>
          </div>
        )}
        {props.data?.targetDate && (
          <div className="flex items-center mb-1 gap-1">
            Target date:
            <div className="text-xs text-gray-400">
              {props.data?.targetDate
                ? moment(props.data?.targetDate).format("DD.MM.YYYY")
                : ""}
            </div>
          </div>
        )}
        {props.data?.description && (
          <div className="max-w-xl">Description: {props.data?.description}</div>
        )}
      </div>
      {props.data?.target !== 0 && props.data?.target && (
        <>
          <div className="mb-4">
            <Statistic
              title="CURRENT PROGRESS"
              value={performance || 0}
              prefix={
                getProgressColor(performance || 0) === 'Green' ? (
                  <ArrowUpOutlined style={{ color: "green" }} />
                ) : getProgressColor(performance || 0) === 'Orange' ? (
                  <ArrowDownOutlined style={{ color: "orange" }} />
                ) : (
                  <ArrowDownOutlined style={{ color: "red" }} />
                )
              }
              suffix="% completed"
            />
          </div>

          <div className="mb-6 max-w-3xl">
            <div className="border rounded">
              <div
                className={
                  "p-3 flex justify-between items-center " +
                  (showHistory && "border-b")
                }
              >
                <div className="flex items-center">
                  <div>
                    <div className="">Target</div>
                    <div className="flex items-center">
                      <div className="text-lg">
                        {targetProgress
                          ? getTargetString(
                              targetProgress,
                              props.data?.scale?.scaleName,
                              props.data?.scale?.scaleUnitAbbreviation
                            )
                          : "0"}
                      </div>
                      <div className="mx-1 text-lg">/</div>
                      <div className="text-lg">
                        {getTargetString(
                          props.data?.target,
                          props.data?.scale?.scaleName,
                          props.data?.scale?.scaleUnitAbbreviation
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  {canUpdate ? (
                    <Button onClick={() => setUpdate(true)} type="primary">
                      Update
                    </Button>
                  ) : (
                    <>
                      <Popover
                        placement="top"
                        content={
                          <div className="text-xs">
                            {props.data?.cascadeFromSplitPlans 
                              ? "Update disabled - This plan uses split plans for progress"
                              : props.data?.cascadeFromTasks
                              ? "Update disabled - This plan uses task completion for progress"
                              : "You can't update this target"
                            }
                          </div>
                        }
                        trigger="hover"
                      >
                        <div className="text-gray-500 text-xs mr-1">
                          <InfoCircleOutlined />
                        </div>
                      </Popover>
                      <Button disabled type="primary">
                        Update
                      </Button>
                    </>
                  )}
                  {permissionAbility(userLevel, "plans", "edit") && (
                    <Tooltip title={"Update History"}>
                      <div
                        className="cursor-pointer text-blue-500 hover:text-blue-600 ml-2"
                        onClick={() => expandHistory()}
                      >
                        <HistoryOutlined />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
              {showHistory && (
                <div className="max-h-64 overflow-auto custom-scroll">
                  <div className="p-3">
                    <ProgressHistoryTable
                      data={history?.targetProgressHistory}
                      handleOk={() => getProgressHistory()}
                      loading={historyLoading}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <div className="mb-6 max-w-3xl">
        <Descriptions column={1} bordered>
          {renderAttributes()}
        </Descriptions>
      </div>
      {update && (
        <UpdateTargetProgressModal
          visible={update}
          handleCancel={() => setUpdate(false)}
          data={props.data}
          targetProgress={targetProgress}
          onFinish={onFinish}
          submit={submit}
        />
      )}
    </div>
  );
}

export default PlanOverview;
